import { useEffect, useState } from 'react';
import { Bell, CheckCheck, ExternalLink, Link2, MessageSquare, RefreshCw, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { api } from '@/lib/api';
import { engagementApi, type ApprovalQueueItem, type InboxItem } from '@/lib/engagement-api';
import { socialApi } from '@/lib/social-api';
import { useNotificationStore } from '@/stores/notifications';
import { cn, formatDate } from '@/lib/utils';
import { platformMeta } from '@/lib/social-platforms';
import type { ApiResponse, Notification } from '@/types';
import { toast } from 'sonner';

type Tab = 'system' | 'comments' | 'approvals';

export function NotificationsPage({ onNavigate }: { onNavigate: (path: string) => void }) {
  const notifications = useNotificationStore((s) => s.notifications);
  const fetchUnreadCount = useNotificationStore((s) => s.fetchUnreadCount);
  const markAsRead = useNotificationStore((s) => s.markAsRead);
  const markAllAsRead = useNotificationStore((s) => s.markAllAsRead);
  const [tab, setTab] = useState<Tab>('system');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [commentsError, setCommentsError] = useState<string | null>(null);
  const [approvalsError, setApprovalsError] = useState<string | null>(null);
  const [inbox, setInbox] = useState<InboxItem[]>([]);
  const [approvals, setApprovals] = useState<ApprovalQueueItem[]>([]);
  const [replyDraft, setReplyDraft] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [sys, comments, queue] = await Promise.all([
          api.get<ApiResponse<Notification[]>>('/notifications?limit=100').catch((e) => ({
            success: false,
            error: e instanceof Error ? e.message : 'Systemmeldungen konnten nicht geladen werden.',
            data: [] as Notification[],
          })),
          engagementApi.inbox().catch((e) => {
            setCommentsError(e instanceof Error ? e.message : 'Kommentare konnten nicht geladen werden.');
            return [] as InboxItem[];
          }),
          engagementApi.approvalQueue().catch((e) => {
            setApprovalsError(e instanceof Error ? e.message : 'Freigaben konnten nicht geladen werden.');
            return [] as ApprovalQueueItem[];
          }),
        ]);
        if (!sys.success) {
          setError(sys.error ?? 'Posteingang konnte nicht geladen werden.');
        } else {
          useNotificationStore.setState({ notifications: sys.data ?? [] });
        }
        setInbox(comments);
        setApprovals(queue);
        await fetchUnreadCount();
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Posteingang konnte nicht geladen werden.');
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [fetchUnreadCount]);

  const openNotification = async (n: Notification) => {
    try {
      if (!n.is_read) await markAsRead(n.id);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Meldung konnte nicht als gelesen markiert werden.');
    }
    if (n.link) onNavigate(n.link.startsWith('/') ? n.link : `/${n.link}`);
  };

  const sorted = [...notifications].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );
  const unread = notifications.filter((n) => !n.is_read).length;
  const unreadComments = inbox.filter((i) => !i.is_read).length;

  const reply = async (item: InboxItem) => {
    const text = (replyDraft[item.id] || '').trim();
    if (!text) return;
    setBusyId(item.id);
    try {
      await engagementApi.replyInbox(item.id, text);
      setInbox((prev) =>
        prev.map((row) => (row.id === item.id ? { ...row, is_read: true, replied_at: new Date().toISOString() } : row))
      );
      setReplyDraft((prev) => ({ ...prev, [item.id]: '' }));
      toast.success('Antwort gesendet.');
      await fetchUnreadCount();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Antwort fehlgeschlagen.');
    } finally {
      setBusyId(null);
    }
  };

  const decideSocial = async (item: ApprovalQueueItem, action: 'approve' | 'reject') => {
    if (item.kind !== 'social_post' || action === 'approve') {
      onNavigate(item.href);
      return;
    }
    setBusyId(item.id);
    try {
      await socialApi.rejectPost(item.entity_id);
      setApprovals((prev) => prev.filter((row) => row.id !== item.id));
      toast.success('Zurück an Entwurf.');
      await fetchUnreadCount();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Aktion fehlgeschlagen.');
    } finally {
      setBusyId(null);
    }
  };

  const copyShareLink = async (item: ApprovalQueueItem) => {
    try {
      const share = await engagementApi.shareApproval({
        kind: item.kind,
        entity_id: item.entity_id,
        campaign_id: item.campaign_id,
      });
      const url = `${window.location.origin}${window.location.pathname}#${share.path}`;
      await navigator.clipboard.writeText(url);
      toast.success('Freigabe-Link kopiert.');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Link konnte nicht erzeugt werden.');
    }
  };

  return (
    <div className="w-full space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-semibold text-foreground">Posteingang</h1>
        <div className="flex flex-wrap gap-2">
          {tab === 'system' && unread > 0 && (
            <Button type="button" variant="outline" size="sm" className="gap-2" onClick={() => void markAllAsRead().catch((e) => toast.error(e instanceof Error ? e.message : 'Meldungen konnten nicht als gelesen markiert werden.'))}>
              <CheckCheck className="h-4 w-4" />
              Alle als gelesen
            </Button>
          )}
          {tab === 'comments' && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={() =>
                engagementApi
                  .syncInbox()
                  .then((r) => {
                    toast.success(`${r.upserted} Kommentare aktualisiert.`);
                    return engagementApi.inbox();
                  })
                  .then((rows) => { setInbox(rows); setCommentsError(null); return fetchUnreadCount(); })
                  .catch((e) => toast.error(e instanceof Error ? e.message : 'Sync fehlgeschlagen.'))
              }
            >
              <RefreshCw className="h-4 w-4" />
              Sync
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {(
          [
            ['system', 'System', unread],
            ['comments', 'Kommentare', unreadComments],
            ['approvals', 'Freigabe', approvals.length],
          ] as const
        ).map(([id, label, count]) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={cn(
              'rounded-lg border px-3 py-1.5 text-sm font-medium',
              tab === id ? 'border-border bg-card-hover text-foreground' : 'border-border text-muted'
            )}
          >
            {label}
            {count > 0 && <span className="ml-2 tabular-nums text-xs">{count}</span>}
          </button>
        ))}
      </div>

      {tab === 'system' && (
        <Card className="w-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Bell className="h-5 w-5 text-foreground" aria-hidden />
              System
            </CardTitle>
            <CardDescription>
              {loading ? 'Wird geladen…' : error ? error : `${sorted.length} Einträge`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-16 animate-pulse rounded-lg bg-card-hover" />
                ))}
              </div>
            ) : sorted.length === 0 ? (
              <p className="text-sm text-muted">Keine Systemmeldungen.</p>
            ) : (
              <ul className="divide-y divide-border">
                {sorted.map((n) => (
                  <li key={n.id} className="py-3 first:pt-0 last:pb-0">
                    <button
                      type="button"
                      onClick={() => void openNotification(n)}
                      className={cn(
                        'flex w-full flex-col gap-1 rounded-lg px-2 py-2 text-left hover:bg-card-hover',
                        !n.is_read && 'bg-card-hover/70'
                      )}
                    >
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <span className="font-medium text-foreground">{n.title}</span>
                        <span className="shrink-0 text-xs text-muted">{formatDate(n.created_at)}</span>
                      </div>
                      {n.message && <span className="text-sm text-muted">{n.message}</span>}
                      {n.link && (
                        <span className="inline-flex items-center gap-1 text-xs text-foreground">
                          Zum Inhalt <ExternalLink className="h-3 w-3" />
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}

      {tab === 'comments' && (
        <Card className="w-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <MessageSquare className="h-5 w-5" aria-hidden />
              Kommentare
            </CardTitle>
            <CardDescription>Antworten aus LinkedIn, Meta und X.</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-sm text-muted">Kommentare werden geladen…</p>
            ) : commentsError ? (
              <p role="alert" className="text-sm text-danger">{commentsError}</p>
            ) : inbox.length === 0 ? (
              <p className="text-sm text-muted">Keine Kommentare. Sync holt neue Einträge von verbundenen Kanälen.</p>
            ) : (
              <ul className="space-y-4">
                {inbox.map((item) => (
                  <li key={item.id} className="rounded-lg border border-border p-3">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          {item.author_name || 'Unbekannt'}
                          <span className="ml-2 text-xs font-normal text-muted">
                            {platformMeta(item.platform as never).label} · {item.kind}
                          </span>
                        </p>
                        <p className="mt-1 text-sm text-foreground">{item.body}</p>
                      </div>
                      {item.occurred_at && (
                        <span className="text-xs text-muted">{formatDate(item.occurred_at)}</span>
                      )}
                    </div>
                    <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                      {item.post_id && (
                        <Button type="button" variant="ghost" size="sm" onClick={() => onNavigate(`/composer/${item.post_id}`)}>
                          Post
                        </Button>
                      )}
                      {item.permalink && (
                        <a href={item.permalink} target="_blank" rel="noreferrer" className="text-sm text-foreground underline-offset-2 hover:underline">
                          Original
                        </a>
                      )}
                    </div>
                    {item.replied_at ? (
                      <p className="mt-2 text-xs text-muted">Beantwortet</p>
                    ) : (
                      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                        <input
                          aria-label={`Antwort an ${item.author_name || 'Unbekannt'}`}
                          className="min-w-0 flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm"
                          placeholder="Antwort"
                          value={replyDraft[item.id] ?? ''}
                          onChange={(e) => setReplyDraft((prev) => ({ ...prev, [item.id]: e.target.value }))}
                        />
                        <Button
                          type="button"
                          size="sm"
                          disabled={busyId !== null || !(replyDraft[item.id] || '').trim()}
                          onClick={() => void reply(item)}
                        >
                          Senden
                        </Button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}

      {tab === 'approvals' && (
        <Card className="w-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <ShieldCheck className="h-5 w-5" aria-hidden />
              Freigabe
            </CardTitle>
            <CardDescription>Social-Posts und Kampagneninhalte, die auf Review warten.</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-sm text-muted">Freigaben werden geladen…</p>
            ) : approvalsError ? (
              <p role="alert" className="text-sm text-danger">{approvalsError}</p>
            ) : approvals.length === 0 ? (
              <p className="text-sm text-muted">Nichts wartet auf Freigabe.</p>
            ) : (
              <ul className="space-y-3">
                {approvals.map((item) => (
                  <li key={item.id} className="flex flex-col gap-3 rounded-lg border border-border p-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <p className="font-medium text-foreground">{item.title}</p>
                      <p className="text-sm text-muted">{item.subtitle}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button type="button" variant="outline" size="sm" onClick={() => onNavigate(item.href)}>
                        Öffnen
                      </Button>
                      <Button type="button" variant="outline" size="sm" className="gap-1" onClick={() => void copyShareLink(item)}>
                        <Link2 className="h-3.5 w-3.5" />
                        Link
                      </Button>
                      {item.kind === 'social_post' && (
                        <>
                          <Button type="button" size="sm" disabled={busyId === item.id} onClick={() => void decideSocial(item, 'approve')}>
                            Beitrag prüfen
                          </Button>
                          <Button type="button" variant="outline" size="sm" disabled={busyId === item.id} onClick={() => void decideSocial(item, 'reject')}>
                            Ablehnen
                          </Button>
                        </>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
